import { useState, useEffect, useCallback, useRef } from 'react';
import qs from 'qs';
import api from 'utils/api';
import ROUTE_PATH from 'routes/config';
import { useApp } from '../contexts/AppContext';
import {
  getCookie,
  setCookie,
  KEY_REFRESH,
  KEY_SESSION,
} from '../tools/WebTool';

const useIdentification = ({
  returnUrl = `${window.location.origin}${ROUTE_PATH.identificationSuccessCallback}`,
  errorUrl = `${window.location.origin}${ROUTE_PATH.identificationFailCallback}`,
  identificationPurposeType,
  identificationSuccessCallback,
  identificationFailCallback,
  identificationExceptCallback,
}) => {
  const {
    auth: { authenticated },
  } = useApp();

  const uniqueKeyRef = useRef(`ident_${Date.now()}_${Math.random()}`);
  const popupRef = useRef(null);

  const [identificationQuery, setIdentificationQuery] = useState({
    authType: 'M',
    deviceType: '',
    returnUrl: addQuery(returnUrl, 'key', uniqueKeyRef.current),
    errorUrl: addQuery(errorUrl, 'key', uniqueKeyRef.current),
  });

  function addQuery(url, key, value) {
    const hasQuery = url.includes('?');
    return `${url}${hasQuery ? '&' : '?'}${key}=${value}`;
  }

  const detectDeviceType = () => {
    const ua = navigator.userAgent;
    const isMobile =
      /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(ua);
    const isDesktop = /Windows|Macintosh|Linux/i.test(ua);
    return isMobile ? 'Mobile' : isDesktop ? '' : '';
  };

  const storeSessionToSessionStorage = () => {
    const session = getCookie(KEY_SESSION);
    const refresh = getCookie(KEY_REFRESH);
    if (session) sessionStorage.setItem(KEY_SESSION, session);
    if (refresh) sessionStorage.setItem(KEY_REFRESH, refresh);
  };

  const restoreSessionFromSessionStorage = () => {
    const session = sessionStorage.getItem(KEY_SESSION);
    const refresh = sessionStorage.getItem(KEY_REFRESH);
    if (session) setCookie(KEY_SESSION, session);
    if (refresh) setCookie(KEY_REFRESH, refresh);
    sessionStorage.removeItem(KEY_SESSION);
    sessionStorage.removeItem(KEY_REFRESH);
  };

  const wrapWithSession = callback => {
    return (...args) => {
      restoreSessionFromSessionStorage();
      if (typeof callback === 'function') {
        callback(...args);
      }
    };
  };

  useEffect(() => {
    setIdentificationQuery(prev => ({
      ...prev,
      authType: 'M',
      deviceType: detectDeviceType(),
      returnUrl: addQuery(returnUrl, 'key', uniqueKeyRef.current),
      errorUrl: addQuery(errorUrl, 'key', uniqueKeyRef.current),
    }));
  }, [returnUrl, errorUrl]);

  useEffect(() => {
    const key = uniqueKeyRef.current;
    if (!window.__identificationGlobalCallbacks) {
      window.__identificationGlobalCallbacks = {};
    }

    window.__identificationGlobalCallbacks[key] = {
      purposeType: identificationPurposeType,
      success: wrapWithSession(identificationSuccessCallback),
      fail: wrapWithSession(identificationFailCallback),
      except: wrapWithSession(identificationExceptCallback),
    };

    return () => {
      delete window.__identificationGlobalCallbacks[key];
    };
  }, [
    identificationPurposeType,
    identificationSuccessCallback,
    identificationFailCallback,
    identificationExceptCallback,
  ]);

  const openPopupIdentification = ({ identificationQuery, authenticated }) => {
    const apiUrl = authenticated
      ? '/v1/au/nice-id/encode'
      : '/v1/na/nice-id/encode';

    // 이미 열린 팝업이 있는지 확인
    if (popupRef.current && !popupRef.current.closed) {
      try {
        popupRef.current.focus();
        alert('이미 본인인증 창이 열려 있습니다. 창을 닫고 다시 시도해주세요.');
      } catch (e) {
        console.warn('팝업 focus 실패:', e);
      }
      return;
    }

    api({
      url: `${apiUrl}?${qs.stringify(identificationQuery)}`,
      method: 'get',
    })
      .then(res => {
        const { data } = res.data;
        const popup = window.open(
          '/identification',
          'popupChk',
          'width=480, height=780, top=100, left=100, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=yes, location=no, scrollbar=no'
        );

        if (!popup) {
          alert('팝업이 차단 되었습니다. 팝업을 허용하고 다시 시도해주세요.');
          return;
        }

        popup._encodeData = data.encodeData;
        popupRef.current = popup; // 열린 팝업 추적
      })
      .catch(err => {
        console.error('본인인증 요청 실패:', err);
      });
  };

  const onClickIdentification = useCallback(
    memberCertificationSeq => {
      // 이벤트 객체인지 확인 (클릭 이벤트로 호출된 경우)
      if (memberCertificationSeq && memberCertificationSeq.nativeEvent) {
        // 새 본인인증 시작 (클릭 이벤트)
        storeSessionToSessionStorage();
        openPopupIdentification({ identificationQuery, authenticated });
        return;
      }

      if (memberCertificationSeq) {
        // 세션 기반 인증 완료 처리 (실제 값이 전달된 경우)
        const key = uniqueKeyRef.current;
        const callbackSet = window.__identificationGlobalCallbacks?.[key];
        if (callbackSet && typeof callbackSet.success === 'function') {
          callbackSet.success(memberCertificationSeq);
        }
        return;
      }

      // memberCertificationSeq가 없는 경우 (원래 로직)
      // 새 본인인증 시작
      storeSessionToSessionStorage();
      openPopupIdentification({ identificationQuery, authenticated });
    },
    [identificationQuery, authenticated]
  );

  return { onClickIdentification };
};

export default useIdentification;
